import { stringifyQuery } from '~/components/tool/Query';
import { formatSubmitData } from '~/components/tool/Message';
import { CreateSpecialistChatRequestQuery } from '~/types/Timeline';
import { SpecialistChatInvoice, SpecialistChatDetail } from '~/types/SpecialistChat';

import { GetMessageResponse, MessageRequest } from '~/types/Message';
import { InvoiceRequestQueryFormData } from '~/types/Invoice';
import { PaymentSubmitResponse } from '~/types/Payment';

export interface SpecialistChatApi {
  /** チャット医療相談の作成 */
  createSpecialistChat: (projectId: string, requestBody: CreateSpecialistChatRequestQuery) => Promise<void>

  /** チャット医療相談詳細情報取得 */
  getSpecialistChatDetail: (projectId: string, medicalConsultingId: string) => Promise<SpecialistChatDetail | null>

  /** チャット医療相談メッセージ取得 */
  getSpecialistChatMessageByProjectId: (projectId: number | string, specialistChatId: number | string, query) => Promise<GetMessageResponse>;

  /** チャット医療相談メッセージ投稿 */
  postSpecialistChatMessageByProjectId: (projectId: number | string, specialistChatId: number | string, body: MessageRequest) => Promise<any>;

  /** チャット医療相談メッセージ削除 */
  removeSpecialistChatMessageByProjectId: (projectId: string, specialistChatId: number | string, messageId: string) => Promise<void>;

  /** チャット医療相談メッセージ復活 */
  putSpecialistChatMessageByProjectId: (projectId: string, specialistChatId: number | string, messageId: string) => Promise<void>;

  /** チャット医療相談 の終了 */
  finishSpecialistChat: (projectId: string, specialistChatId: string) => Promise<void>

  /** チャット医療相談 の削除 */
  deleteSpecialistChat: (projectId: string, specialistChatId: string) => Promise<void>

  /** 診療明細情報取得 */
  getSpecialistChatInvoice: (projectId: string, specialistChatId: string) => Promise<SpecialistChatInvoice | null>

  /** チャット医療相談 明細の送信 */
  postSpecialistChatInvoice: (projectId: string, specialistChatId: string, requestBody: InvoiceRequestQueryFormData) => Promise<void>

  /** 支払いの送信 */
  postSpecialistChatInvoicePayment: (projectId: string, specialistChatId: string) => Promise<PaymentSubmitResponse>

  /** 支払いのDB更新 */
  updateSpecialistChatInvoicePayment: (projectId: string, specialistChatId: string, stripePaymentResultId: string) => Promise<void>
}

export const useSpecialistChatApi = (fetch, axios): SpecialistChatApi => {
  const createSpecialistChat = async (projectId: string, requestBody: CreateSpecialistChatRequestQuery): Promise<void> => {
    await fetch(`/projects/${projectId}/specialistChat`, requestBody);
  };

  const getSpecialistChatDetail = async (projectId: string, specialistChatId: string): Promise<SpecialistChatDetail | null> => {
    try {
      const { data } = await fetch(`/projects/${projectId}/specialistChat/${specialistChatId}`);
      return data;
    } catch ({ response }) {
      if ((response as any)?.status !== 401 || (response as any)?.data.statusCode !== 401) {
        throw new Error('チャット医療相談詳細情報が取得できませんでした');
      }
      return null;
    }
  };

  const getSpecialistChatMessageByProjectId = async (projectId, specialistChatId, query): Promise<GetMessageResponse> => {
    const response = await fetch(`/projects/${projectId}/specialistChat/${specialistChatId}/messages${stringifyQuery(query)}`);

    return response.data;
  };

  const postSpecialistChatMessageByProjectId = async (projectId, specialistChatId, body: MessageRequest): Promise<any> => {
    const response = await fetch(`/projects/${projectId}/specialistChat/${specialistChatId}/messages`, formatSubmitData(body));

    return response.data;
  };

  const removeSpecialistChatMessageByProjectId = async (projectId, specialistChatId, messageId): Promise<void> => {
    await axios.delete(`/projects/${projectId}/specialistChat/${specialistChatId}/messages/${messageId}`);
  };

  const putSpecialistChatMessageByProjectId = async (projectId, specialistChatId, messageId): Promise<void> => {
    await axios.put(`/projects/${projectId}/specialistChat/${specialistChatId}/messages/${messageId}`);
  };

  const finishSpecialistChat = async (projectId: string, specialistChatId: string): Promise<void> => {
    await axios.put(`/projects/${projectId}/specialistChat/${specialistChatId}`);
  };

  const deleteSpecialistChat = async (projectId: string, specialistChatId: string): Promise<void> => {
    await axios.delete(`/projects/${projectId}/specialistChat/${specialistChatId}`);
  };

  const getSpecialistChatInvoice = async (projectId: string, specialistChatId: string): Promise<SpecialistChatInvoice | null> => {
    try {
      const { data } = await fetch(`/projects/${projectId}/specialistChat/${specialistChatId}/payments`);
      return data;
    } catch ({ response }) {
      if ((response as any)?.status !== 401 || (response as any)?.data.statusCode !== 401) {
        throw new Error('診療明細情報が取得できませんでした');
      }
      return null;
    }
  };

  const postSpecialistChatInvoice = async (projectId: string, specialistChatId: string, requestBody: InvoiceRequestQueryFormData): Promise<void> => {
    await fetch(`/projects/${projectId}/specialistChat/${specialistChatId}/payments`, requestBody);
  };

  const postSpecialistChatInvoicePayment = async (projectId: string, specialistChatId: string): Promise<PaymentSubmitResponse> => {
    const response = await fetch(`/projects/${projectId}/specialistChat/${specialistChatId}/paymentSubmit`, {});
    return response.data
  };

  const updateSpecialistChatInvoicePayment = async (projectId: string, specialistChatId: string, stripePaymentResultId: string): Promise<void> => {
    await fetch(`/projects/${projectId}/specialistChat/${specialistChatId}/paymentUpdate/${stripePaymentResultId}`, {});
  };

  return {
    createSpecialistChat,
    getSpecialistChatDetail,
    getSpecialistChatMessageByProjectId,
    postSpecialistChatMessageByProjectId,
    removeSpecialistChatMessageByProjectId,
    putSpecialistChatMessageByProjectId,
    finishSpecialistChat,
    deleteSpecialistChat,
    getSpecialistChatInvoice,
    postSpecialistChatInvoice,
    postSpecialistChatInvoicePayment,
    updateSpecialistChatInvoicePayment
  };
};
